import React, { useState, useRef, useEffect } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { router } from '@inertiajs/react';
import { File, MoreVertical, Trash2, Download, Edit2, FileImage, FileText, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import RenameFileDialog from '@/components/RenameFileDialog';
import ShareDialog from '@/components/ShareDialog';

interface ShareLink {
    id: number;
    token: string;
    url: string;
    expires_at: string | null;
    access_count: number;
}

interface FileItem {
    id: number;
    name: string;
    description: string | null;
    size: string;
    size_bytes: number;
    type: string;
    extension: string;
    is_image: boolean;
    is_pdf: boolean;
    created_at: string;
    url: string | null;
    thumbnail_url: string | null;
    download_url: string;
    shared: boolean;
    share_link: ShareLink | null;
}

interface FileListProps {
    files: FileItem[];
    selectedIds: number[];
    onToggleSelect: (id: number, isCtrlOrCmd: boolean) => void;
    isSelected: (id: number) => boolean;
}

// Tailwind breakpoints: lg=1024 → 6 cols, md=768 → 4 cols, default → 2 cols
function useColumnCount() {
    const getCount = () => {
        if (typeof window === 'undefined') return 6;
        if (window.innerWidth >= 1024) return 6;
        if (window.innerWidth >= 768) return 4;
        return 2;
    };
    const [cols, setCols] = useState(getCount);
    useEffect(() => {
        const handler = () => setCols(getCount());
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    return cols;
}

// Estimated row height: image rows ~200px, non-image ~140px.
// We use a uniform estimate; the virtualizer will auto-correct via measureElement.
const ROW_ESTIMATE_PX = 200;

interface FileCardProps {
    file: FileItem;
    isSelected: boolean;
    onToggleSelect: (id: number, isCtrlOrCmd: boolean) => void;
    onDelete: (file: FileItem, e: React.MouseEvent) => void;
    onDownload: (file: FileItem, e: React.MouseEvent) => void;
    onRename: (file: FileItem) => void;
    onShare: (file: FileItem) => void;
}

function FileCard({ file, isSelected, onToggleSelect, onDelete, onDownload, onRename, onShare }: FileCardProps) {
    const handleClick = (e: React.MouseEvent) => onToggleSelect(file.id, e.ctrlKey || e.metaKey);

    const handleDragStart = (e: React.DragEvent) => {
        if (!isSelected) onToggleSelect(file.id, false);
        e.dataTransfer.effectAllowed = 'move';
    };

    const getFileIcon = () => {
        if (file.is_image) return <FileImage className="h-8 w-8 text-blue-500" />;
        if (file.is_pdf) return <FileText className="h-8 w-8 text-red-500" />;
        return <File className="h-8 w-8 text-muted-foreground" />;
    };

    return (
        <div
            className={`group relative border rounded-lg p-4 transition-colors cursor-pointer ${
                isSelected ? 'bg-primary/10 border-primary' : 'hover:bg-accent'
            }`}
            onClick={handleClick}
            draggable
            onDragStart={handleDragStart}
        >
            <div className="flex items-start justify-between">
                {getFileIcon()}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                            <MoreVertical className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onShare(file); }}>
                            <Share2 className="h-4 w-4 mr-2" />
                            {file.shared ? 'Manage Share' : 'Share'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={(e) => onDownload(file, e)}>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onRename(file); }}>
                            <Edit2 className="h-4 w-4 mr-2" />
                            Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => onDelete(file, e)} className="text-destructive">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {file.is_image && (file.thumbnail_url || file.url) && (
                <div className="mt-2 mb-2">
                    <img
                        src={file.thumbnail_url || file.url!}
                        alt={file.name}
                        className="w-full h-24 object-cover rounded"
                        loading="lazy"
                    />
                </div>
            )}

            <div className="mt-2">
                <p className="font-medium text-sm truncate" title={file.name}>{file.name}</p>
                <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-muted-foreground">{file.size}</p>
                    {file.shared && <Share2 className="h-3 w-3 text-primary" />}
                </div>
            </div>

            {isSelected && (
                <div className="absolute top-2 left-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                </div>
            )}
        </div>
    );
}

export default function FileList({ files, selectedIds, onToggleSelect, isSelected }: FileListProps) {
    const [renamingFile, setRenamingFile] = useState<FileItem | null>(null);
    const [sharingFile, setSharingFile] = useState<FileItem | null>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const cols = useColumnCount();
    const rowCount = Math.ceil(files.length / cols);

    const rowVirtualizer = useWindowVirtualizer({
        count: rowCount,
        estimateSize: () => ROW_ESTIMATE_PX,
        overscan: 3,
        scrollMargin: listRef.current?.offsetTop ?? 0,
    });

    const handleDelete = (file: FileItem, e: React.MouseEvent) => {
        e.stopPropagation();
        if (confirm(`Are you sure you want to delete "${file.name}"?`)) {
            router.delete(`/uploaded_files/${file.id}`);
        }
    };

    const handleDownload = (file: FileItem, e: React.MouseEvent) => {
        e.stopPropagation();
        window.location.href = file.download_url;
    };

    // Kolumny gridu jako inline style – muszą być zsynchronizowane z cols z JS
    const gridStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gap: '1rem',
    };

    return (
        <>
            <div>
                <h2 className="text-sm font-semibold text-muted-foreground mb-3">
                    Files
                </h2>

                {/* Kontener o wysokości całej listy – virtualizer renderuje tylko widoczne wiersze */}
                <div
                    ref={listRef}
                    style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}
                >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        const startIdx = virtualRow.index * cols;
                        const rowFiles = files.slice(startIdx, startIdx + cols);

                        return (
                            <div
                                key={virtualRow.key}
                                data-index={virtualRow.index}
                                ref={rowVirtualizer.measureElement}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    transform: `translateY(${virtualRow.start - rowVirtualizer.options.scrollMargin}px)`,
                                    paddingBottom: '1rem', // gap między wierszami
                                }}
                            >
                                <div style={gridStyle}>
                                    {rowFiles.map((file) => (
                                        <FileCard
                                            key={file.id}
                                            file={file}
                                            isSelected={isSelected(file.id)}
                                            onToggleSelect={onToggleSelect}
                                            onDelete={handleDelete}
                                            onDownload={handleDownload}
                                            onRename={setRenamingFile}
                                            onShare={setSharingFile}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {renamingFile && (
                <RenameFileDialog file={renamingFile} onClose={() => setRenamingFile(null)} />
            )}

            {sharingFile && (
                <ShareDialog
                    file={sharingFile}
                    onClose={() => {
                        setSharingFile(null);
                        router.reload({ only: ['files'] });
                    }}
                />
            )}
        </>
    );
}
